/**
 * Canonical UI language name → ISO code map. Single source of truth — import
 * this instead of redefining the map (services/api, displayLanguage, etc.).
 */
export const LANGUAGE_CODES: Record<string, string> = {
  English: 'en',
  Telugu: 'te',
  Kannada: 'kn',
  Tamil: 'ta',
  Hindi: 'hi',
  Malayalam: 'ml',
  Oriya: 'or',
  Bengali: 'bn',
  Punjabi: 'pa',
  Gujarati: 'gu',
};

/**
 * Language codes in the order the API's per-language *bitmask* fields use —
 * Telugu first, NOT the display order of LANGUAGE_CODES above.
 *
 * The server builds `autoTranslatedFlags` by walking its own language list
 * (jnana-serverless/src/libs/types/languages.ts) and writing '1' at a
 * language's index when AWS Translate produced that translation. The same
 * order generates the `${code}Text` fields in config/nodeTypes.ts. Reading a
 * mask against the wrong order silently mislabels which facts are machine
 * translated, so languages.test.ts pins this against the field definitions.
 */
export const LANGUAGE_BITMASK_ORDER = [
  'te', 'en', 'kn', 'ta', 'hi', 'ml', 'or', 'bn', 'pa', 'gu',
] as const;

/**
 * Endonyms — each language's name written in its own script.
 *
 * Mirrors `nativeName` on jnana-native's `allLanguages`
 * (jnana-native/src/types/languages.ts) character for character. Used wherever
 * the reader is choosing a language rather than being told about one: a Telugu
 * speaker looking for their meaning scans for "తెలుగు", not for "Telugu".
 * English stays "English" — it has no other endonym.
 */
export const LANGUAGE_ENDONYMS: Record<string, string> = {
  te: 'తెలుగు',
  en: 'English',
  kn: 'ಕನ್ನಡ',
  ta: 'தமிழ்',
  hi: 'हिन्दी',
  ml: 'മലയാളം',
  or: 'ଓଡ଼ିଆ',
  bn: 'বাংলা',
  pa: 'ਪੰਜਾਬੀ',
  gu: 'ગુજરાતી',
};

/** English name for a language code ("te" → "Telugu"); the code itself if unknown. */
export const languageNameForCode = (code: string): string => {
  const entry = Object.entries(LANGUAGE_CODES).find(([, c]) => c === code);
  return entry ? entry[0] : code;
};

/** Endonym for a language code, falling back to the English name, then the code. */
export const languageEndonymForCode = (code: string): string =>
  LANGUAGE_ENDONYMS[code] ?? languageNameForCode(code);

/**
 * How to name a language to everyone at once: "Telugu (తెలుగు)".
 *
 * A control labelled only "తెలుగు" is the right thing for a Telugu reader to
 * scan for and unpronounceable to a screen reader running an English voice, so
 * accessible names carry both. English collapses to just "English" — its
 * endonym IS its English name, and "English (English)" reads as a bug.
 */
export const languageFullLabel = (code: string): string => {
  const name = languageNameForCode(code);
  const endonym = languageEndonymForCode(code);
  return endonym === name ? name : `${name} (${endonym})`;
};

/**
 * Decode one language's bit out of a per-language flag mask. Returns false for
 * anything that is not a well-formed mask covering the requested language —
 * absent, truncated by bad data, or a language the mask predates.
 */
export const isFlaggedForLanguage = (
  mask: unknown,
  languageCode: string
): boolean => {
  if (typeof mask !== 'string') return false;
  const index = LANGUAGE_BITMASK_ORDER.indexOf(
    languageCode as (typeof LANGUAGE_BITMASK_ORDER)[number]
  );
  if (index < 0) return false;
  if (mask.length !== LANGUAGE_BITMASK_ORDER.length) return false;
  return mask[index] === '1';
};

/** Resolve a language name ("Telugu") or code ("te") to a code; defaults to 'en'. */
export const toLanguageCode = (nameOrCode: string | null | undefined): string => {
  if (!nameOrCode) return 'en';
  return LANGUAGE_CODES[nameOrCode] ?? nameOrCode;
};
